/* eslint-disable camelcase */
import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { helpTypesToServices, SeekerState } from '@/types/seeker';
import { AppDispatch } from '@/types/app';
import logger from '@/lib/clientLogger';
import { seekerCreate } from '@/__generated__/seekerCreate';
import {
  AddressInput,
  EnrollmentSource,
  PetCareSubtype,
  SeekerCreateInput,
  ServiceType,
} from '@/__generated__/globalTypes';
import { SEEKER_CREATE } from '@/components/request/GQL';
import {
  VerticalsAbbreviation,
  CARE_DATES,
  SKIP_AUTH_CONTEXT_KEY,
  ERROR_CREATING_ACCOUNT,
  SERVICE_TYPE_BY_VERTICAL,
  FLOWS,
} from '@/constants';
import { handleAccountCreationValidationErrors } from '@/utilities/accountCreationValidationErrorHelper';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import {
  useAppDispatch,
  useFlowState,
  useSeekerCCState,
  useSeekerHKState,
  useSeekerPCState,
  useSeekerState,
  useSeekerTUState,
} from '@/components/AppState';
import useLoginRedirect from '@/components/hooks/useLoginRedirect';
import useIsSinglePageEnrollment from '@/components/hooks/useIsSinglePageEnrollment';
import { CareKind, DefaultCareKind, Child } from '@/types/seekerCC';
import { logCareEvent } from '@/utilities/amplitudeAnalyticsHelper';
import { GtmService, GtmEvent } from '@/utilities/GtmService';
import {
  getSeekerAccountCreatePayload,
  SeekerCreatePayload,
} from '@/utilities/getSeekerAccountCreatePayload';
import { constructChildDobForGtmEvent, getDependentCountForGtmEvent } from '@/utilities/dobHelper';
import {
  AccountCreationGeneralFormProps,
  FormNameValues,
} from '@/components/pages/seeker/interfaces';
import socialProviderStore, { IdentityProviders } from '@/lib/SocialProviderStore';
import { getSetSeekerNameAction } from '@/utilities/actionUtils';
import useSetCareMidCookie, { MemberType } from '@/components/hooks/useSetCareMidCookie';
import { SignUpType } from '../pages/seeker/cc/account-creation/utils/utils';

interface eventExtraDataProps {
  memberType: string;
  seen_commercial?: string;
  sign_up_type?: SignUpType;
  sign_up_method?: IdentityProviders | SignUpType.EMAIL;
}

export function sendFailureEvent(
  state: SeekerState,
  dispatch: AppDispatch,
  isSinglePageEnrollment: boolean = false
) {
  if (!state.initialAccountCreationFailed) {
    dispatch({ type: 'setInitialAccountCreationFailed', initialAccountCreationFailed: true });
    logger.info({
      event: 'seekerAccountCreationFailedInitialAttempt',
      screen: isSinglePageEnrollment ? 'singlePageEnrollment' : 'enrollment',
    });
  }
  logger.error({
    event: 'seekerAccountCreationFailed',
    screen: isSinglePageEnrollment ? 'singlePageEnrollment' : 'enrollment',
  });
}

function sendAttemptEvent(
  state: SeekerState,
  dispatch: AppDispatch,
  isSinglePageEnrollment: boolean
) {
  if (!state.initialAccountCreationAttempted) {
    dispatch({
      type: 'setInitialAccountCreationAttempted',
      initialAccountCreationAttempted: true,
    });
    logger.info({
      event: 'seekerAccountCreationInitialAttempt',
      screen: isSinglePageEnrollment ? 'singlePageEnrollment' : 'enrollment',
    });
  }
  logger.info({
    event: 'seekerAccountCreationAttempt',
    screen: isSinglePageEnrollment ? 'singlePageEnrollment' : 'enrollment',
  });
}

export const useSeekerAccountCreation = ({
  vertical,
  nextPageURL,
  setFormikSubmittingFalse,
  setErrorMessage,
}: AccountCreationGeneralFormProps) => {
  const dispatch = useAppDispatch();
  const seekerState = useSeekerState();
  const { flowName, isSocialEnrollment } = useFlowState();
  const { tasks, bedrooms, bathrooms } = useSeekerHKState();
  const { ageLevel, selectedSubjects } = useSeekerTUState();
  const { serviceType } = useSeekerPCState();
  const { careChildrenDOB, careExpecting } = useSeekerCCState();
  const { pets } = useSeekerPCState();
  const { isSeekerSinglePageEnrollment } = useIsSinglePageEnrollment();
  const redirectToLogin =
    vertical === 'IB' || (isSocialEnrollment && vertical !== 'DC')
      ? useLoginRedirect({ nextPageURL }, true)
      : useLoginRedirect({ nextPageURL });
  const router = useRouter();
  const [seekerCreateInput, setSeekerCreateInput] = useState<SeekerCreateInput | null>(null);

  const getDependentCountForPetCare = () => {
    if (pets) {
      return (pets.cats ?? 0) + (pets.dogs ?? 0) + (pets.other ?? 0);
    }
    return 0;
  };

  const handleAccountCreationSuccess = useCallback(
    async (response: seekerCreate) => {
      const { seekerCreate: res } = response;

      if (res.__typename === 'SeekerCreateError') {
        sendFailureEvent(seekerState, dispatch, isSeekerSinglePageEnrollment);
        handleAccountCreationValidationErrors(res.errors, setErrorMessage);

        setFormikSubmittingFalse();
        return;
      }

      // resetting social enrollment flag
      if (isSocialEnrollment) {
        dispatch({ type: 'setIsSocialEnrollment', isSocialEnrollment: undefined });
      }

      dispatch({ type: 'setMemberId', memberId: res.memberId });
      dispatch({ type: 'setExternalId', externalId: res.externalId });
      dispatch({ type: 'setMemberUuid', memberUuid: res.memberUuid });
      AnalyticsHelper.setMemberId(res.memberId);
      dispatch({
        type: 'setSeekerParams',
        servicesNeeded: helpTypesToServices(seekerState.helpTypes),
        typeOfCare: seekerState.typeOfCare,
        zip: seekerState.zipcode,
      });
      // Add 'care_mid' cookie for the Akamai logs.
      useSetCareMidCookie(res.memberUuid, MemberType.SEEKER);

      logger.info({
        event: 'seekerAccountCreationSuccessful',
        memberId: res.memberId,
        screen: isSeekerSinglePageEnrollment ? 'singlePageEnrollment' : 'enrollment',
      });

      const getSubServiceId = (verticalType: VerticalsAbbreviation) => {
        let subServiceId;

        switch (verticalType) {
          case 'TU':
            subServiceId = ageLevel;
            break;
          case 'PC':
            subServiceId = serviceType;
            break;
          default:
            subServiceId = seekerCreateInput?.subServiceType!;
        }

        return subServiceId;
      };

      await GtmService.queueDefinedEvent(GtmEvent.SeekerBasic, {
        email: seekerCreateInput?.email,
        memberId: res.memberId,
        intent: seekerCreateInput?.careDate!,
        serviceId: SERVICE_TYPE_BY_VERTICAL[vertical],
        subServiceId: getSubServiceId(vertical),
        firstName: seekerCreateInput?.firstName,
        lastName: seekerCreateInput?.lastName,
        city: seekerState?.city,
        state: seekerState?.state,
        memberUuid: res?.memberUuid,
        zipCode: seekerState?.zipcode,
        ...(vertical === 'HK' && {
          helpType: tasks.join(),
          dependentCount: (bathrooms ?? 0) + (bedrooms ?? 0),
        }),
        ...(vertical === 'CC' && {
          childDob: constructChildDobForGtmEvent(careExpecting, careChildrenDOB),
          dependentCount: getDependentCountForGtmEvent(careExpecting, careChildrenDOB),
        }),
        ...(vertical === 'PC' && {
          dependentCount: getDependentCountForPetCare(),
        }),
        ...(vertical === 'TU' && {
          dependentCount: selectedSubjects?.length ?? 0,
        }),
        hdyhau: seekerCreateInput?.howDidYouHearAboutUs,
        numOfProviders: seekerState.numOfProviders,
        loginType: socialProviderStore.getLoginType(),
      });
      redirectToLogin(res.authToken, res.oneTimeToken);
    },
    [seekerState, seekerCreateInput]
  );

  const seekerNameActionType = getSetSeekerNameAction(SERVICE_TYPE_BY_VERTICAL[vertical]);

  const handleAccountCreationError = useCallback(() => {
    setErrorMessage(ERROR_CREATING_ACCOUNT);
    sendFailureEvent(seekerState, dispatch, isSeekerSinglePageEnrollment);
    if (seekerNameActionType) {
      dispatch({
        type: seekerNameActionType,
        firstName: '',
        lastName: '',
      });
    }

    setFormikSubmittingFalse();
  }, [seekerState]);

  const [seekerAccountCreate] = useMutation<seekerCreate, { input: SeekerCreateInput }>(
    SEEKER_CREATE,
    {
      context: { [SKIP_AUTH_CONTEXT_KEY]: true },
      onCompleted: handleAccountCreationSuccess,
      onError: handleAccountCreationError,
    }
  );

  const executeSeekerAccountCreate = ({
    values,
    zipcode,
    careDate,
    referrerCookie,
    careKind,
    skipAccountCreation,
    address,
    careRecipients,
    isExpecting,
    additionalVerticals,
    gatedByPaywall,
  }: {
    values: FormNameValues;
    zipcode: string;
    careDate?: CARE_DATES;
    referrerCookie: string | null | undefined;
    careKind: CareKind | PetCareSubtype | undefined;
    skipAccountCreation: boolean;
    address?: AddressInput;
    careRecipients?: Child[];
    isExpecting?: boolean | null;
    additionalVerticals?: Array<ServiceType>;
    gatedByPaywall?: boolean;
  }) => {
    setErrorMessage(null);
    sendAttemptEvent(seekerState, dispatch, isSeekerSinglePageEnrollment);
    if (seekerNameActionType) {
      dispatch({
        type: seekerNameActionType,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      });
    }
    if (values.howDidYouHearAboutUs) {
      dispatch({
        type: 'setHdyhau',
        hdyhau: values.howDidYouHearAboutUs,
      });
    }
    if (skipAccountCreation) {
      router.push(nextPageURL);
    } else {
      const { firstName, lastName, email, howDidYouHearAboutUs, password } = values;
      const payloadInput = {
        zipcode,
        vertical: vertical as VerticalsAbbreviation, // need to cast type here as its intially undefined
        firstName,
        lastName,
        email,
        password,
        howDidYouHearAboutUs,
        careKind,
        careDate,
        referrerCookie,
        careRecipients,
        isExpecting,
        additionalVerticals,
      } as SeekerCreatePayload;

      if (flowName === FLOWS.SEEKER_INSTANT_BOOK_PET_CARE.name && address) {
        payloadInput.addressLine1 = `${address.addressLine1}${
          address.addressLine2 ? `, ${address.addressLine2}` : ''
        }`;
        payloadInput.enrollmentSource = EnrollmentSource.INSTANT_BOOK;
      }

      if (
        seekerState.queryParamMetadata?.comesFromIbLandingPage &&
        careKind === DefaultCareKind.ONE_TIME_BABYSITTERS
      ) {
        payloadInput.vertical = 'IB';
      }

      if (gatedByPaywall) {
        payloadInput.gatedByPaywall = gatedByPaywall;
      }

      const payload = getSeekerAccountCreatePayload(payloadInput);

      setSeekerCreateInput(payload.variables.input);
      seekerAccountCreate(payload);

      const { appleId, googleId } = payload.variables.input;

      const eventExtraData: eventExtraDataProps = {
        memberType: 'Seeker',
        sign_up_type: SignUpType.EMAIL,
        sign_up_method: SignUpType.EMAIL,
        ...(isSeekerSinglePageEnrollment && {
          screen_name: 'Single Page Enrollment',
          enrollment_step: null,
        }),
      };

      if (appleId || googleId) {
        eventExtraData.sign_up_type = SignUpType.SOCIAL;
        if (appleId) eventExtraData.sign_up_method = IdentityProviders.APPLE;
        if (googleId) eventExtraData.sign_up_method = IdentityProviders.GOOGLE;
      }

      logCareEvent(
        'Member Enrolled',
        vertical === 'DC' ? 'day care account creation - name' : 'First and Last Name',
        eventExtraData,
        'Join now',
        true
      );
    }
  };

  return { executeSeekerAccountCreate };
};
